/**
 * Shared patient journey — keep in sync with Flutter `patient_journey.dart`
 * and brain `02-patient-flow.md`.
 */
export const PATIENT_JOURNEY_STEPS = [
  { id: "documents", key: "documents", label: "Documents", short: "Docs", desc: "Upload & pick procedure" },
  { id: "budget", key: "budget", label: "Draft budget", short: "Draft", desc: "Save amount & location" },
  { id: "admin", key: "admin", label: "Admin check", short: "Admin", desc: "Each document verified" },
  { id: "replies", key: "replies", label: "Notify & replies", short: "Notify", desc: "Confirm, then doctors respond" },
  { id: "book", key: "book", label: "Choose & book", short: "Book", desc: "Pick a doctor & book visit" },
];

/** @returns {number} 0–4 — create-wizard → journey strip (Docs+Cat=0, Budget+Details+Review=1). */
export function journeyActiveIndexForWizardStep(wizardStep) {
  const safe = Math.min(Math.max(0, Number(wizardStep) || 0), 4);
  return safe <= 1 ? 0 : 1;
}

/**
 * @param {string} [workflow]
 * @param {{ wizardStep?: number }} [opts] — when DRAFT, pass wizardStep so Review stays on Budget (1).
 * @returns {number} 0–4
 */
export function journeyActiveIndexFromWorkflow(workflow, opts = {}) {
  const u = String(workflow || "").trim().toUpperCase();
  if (!u || u === "DRAFT") {
    return opts.wizardStep != null
      ? journeyActiveIndexForWizardStep(opts.wizardStep)
      : 0;
  }
  if (u === "PENDING_ADMIN_REVIEW" || u === "REJECTED") return 2;
  // VERIFIED: budget already saved — confirm to notify matching doctors.
  if (u === "VERIFIED" || u === "BIDDING") return 3;
  if (u === "BID_SELECTED") return 4;
  if (u === "BOOKED" || u === "VISIT_COMPLETED") return 4;
  return 0;
}

/**
 * @returns {{ documents: string, budget: string, admin: string, replies: string, book: string }}
 * each 'done' | 'current' | 'todo'
 */
export function getPatientJourneyStepClasses(workflow) {
  const u = String(workflow || "").toUpperCase() || "DRAFT";
  const active = journeyActiveIndexFromWorkflow(u);
  const allDone = u === "BOOKED" || u === "VISIT_COMPLETED";
  const keys = ["documents", "budget", "admin", "replies", "book"];
  const out = {};
  keys.forEach((k, i) => {
    if (allDone) out[k] = "done";
    else if (i < active) out[k] = "done";
    else if (i === active) out[k] = "current";
    else out[k] = "todo";
  });
  return out;
}

export function journeyPillText(workflow) {
  const u = String(workflow || "").toUpperCase();
  if (u === "BIDDING" || u === "BID_SELECTED") return "Doctors replying";
  if (u === "VERIFIED") return "Ready to notify doctors";
  if (u === "PENDING_ADMIN_REVIEW") return "Admin checking documents";
  if (u === "DRAFT" || !u) return "Draft";
  if (u === "BOOKED") return "Booked";
  if (u === "VISIT_COMPLETED") return "Visit completed";
  if (u === "REJECTED") return "Needs attention";
  return String(workflow || "—").replace(/_/g, " ");
}

export function journeyBannerPill(workflow) {
  const u = String(workflow || "").toUpperCase();
  if (!String(workflow || "").trim() || u === "DRAFT") return "Draft";
  if (u === "BOOKED") return "Complete";
  if (u === "VISIT_COMPLETED") return "Visit completed";
  if (u === "REJECTED") return "Needs attention";
  return "In progress";
}
